import { AREAS, GRENADES, IMPORTANCES, STATUSES, type Area, type Combo, type Grenade, type Importance, type LearnStatus, type Stuff } from "@69ly/shared";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router";

export interface Filters {
  grenades: Grenade[];
  area: Area | "all";
  side: "t" | "ct" | "both";
  status: LearnStatus | "none" | "any";
  /** Generic, useful, situational: several can be picked; none = all. */
  importance: Importance[];
  q: string;
  /** Singles or combos on the Utility map: one or the other, never both (too many markers). */
  kind: "singles" | "combos";
  /** Only what this player wrote (their user id). */
  by: string | null;
  /** Only what is thrown from my spots and the callouts next to them (0.27.0). */
  mine: boolean;
}

const list = <T extends string>(value: string | null, allowed: readonly T[]): T[] =>
  (value ?? "").split(",").filter((v): v is T => (allowed as readonly string[]).includes(v));

/** Filters live in the address, so a filtered view can be shared or bookmarked. */
export function useFilters() {
  const [params, setParams] = useSearchParams();
  const filters: Filters = useMemo(() => {
    const area = params.get("area");
    const side = params.get("side");
    const status = params.get("status");
    return {
      grenades: list(params.get("g"), GRENADES),
      area: area && (AREAS as readonly string[]).includes(area) ? (area as Area) : "all",
      side: side === "t" || side === "ct" ? side : "both",
      status: status && ([...STATUSES, "none"] as string[]).includes(status) ? (status as Filters["status"]) : "any",
      importance: list(params.get("imp"), IMPORTANCES),
      q: params.get("q") ?? "",
      // A link to a combo (?c=) opens on the combos.
      kind: params.get("type") === "combos" || (params.get("type") !== "singles" && params.has("c")) ? "combos" : "singles",
      by: params.get("by") || null,
      mine: params.get("mine") === "1",
    };
  }, [params]);

  const update = useCallback(
    (change: Partial<Filters>) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const f = { ...filters, ...change };
          const set = (key: string, value: string, empty: string) => (value && value !== empty ? next.set(key, value) : next.delete(key));
          set("g", f.grenades.join(","), "");
          set("area", f.area, "all");
          set("side", f.side, "both");
          set("status", f.status, "any");
          set("imp", f.importance.join(","), "");
          set("q", f.q.trim(), "");
          set("type", f.kind, "singles");
          set("by", f.by ?? "", "");
          set("mine", f.mine ? "1" : "", "");
          return next;
        },
        { replace: true },
      );
    },
    [filters, setParams],
  );

  const active =
    filters.grenades.length > 0 ||
    filters.area !== "all" ||
    filters.side !== "both" ||
    filters.status !== "any" ||
    filters.importance.length > 0 ||
    filters.q.trim() !== "" ||
    filters.by !== null ||
    filters.mine;
  // The type switch is not a filter: clearing filters keeps it.
  const clear = useCallback(() => update({ grenades: [], area: "all", side: "both", status: "any", importance: [], q: "", by: null, mine: false }), [update]);
  return { filters, update, active, clear };
}

/** `places` adds the callouts a stuff is thrown from and lands on to what the search looks through. */
export function applyFilters(stuffs: Stuff[], f: Filters, places?: (s: Stuff) => string): Stuff[] {
  const q = f.q.trim().toLowerCase();
  return stuffs.filter(
    (s) =>
      (f.grenades.length === 0 || f.grenades.includes(s.grenade)) &&
      (f.area === "all" || s.area === f.area) &&
      (f.side === "both" || s.side === f.side) &&
      (f.status === "any" || (f.status === "none" ? s.myStatus === null : s.myStatus === f.status)) &&
      (f.importance.length === 0 || f.importance.includes(s.importance)) &&
      (f.by === null || s.author.id === f.by) &&
      (q === "" || s.title.toLowerCase().includes(q) || s.notes.toLowerCase().includes(q) || (places?.(s).toLowerCase().includes(q) ?? false)),
  );
}

/** My status on a combo, from its singles: learned when all are, otherwise the furthest one started. */
export function comboStatus(c: Combo): LearnStatus | null {
  const statuses = c.steps.map((s) => s.stuff.myStatus);
  if (statuses.length && statuses.every((s) => s === "learned")) return "learned";
  if (statuses.some((s) => s === "practising" || s === "learned")) return "practising";
  if (statuses.some((s) => s === "to_learn")) return "to_learn";
  return null;
}

export function applyComboFilters(combos: Combo[], f: Filters): Combo[] {
  const q = f.q.trim().toLowerCase();
  return combos.filter((c) => {
    const status = comboStatus(c);
    return (
      (f.grenades.length === 0 || c.steps.some((s) => f.grenades.includes(s.stuff.grenade))) &&
      (f.area === "all" || c.area === f.area) &&
      (f.side === "both" || c.side === f.side) &&
      (f.status === "any" || (f.status === "none" ? status === null : status === f.status)) &&
      (f.importance.length === 0 || f.importance.includes(c.importance)) &&
      (f.by === null || c.author.id === f.by) &&
      (q === "" || c.title.toLowerCase().includes(q) || c.notes.toLowerCase().includes(q))
    );
  });
}

/** Who wrote something here, by name: the choices of the "Added by" filter. */
export function authorsOf(items: { author: { id: string; name: string } }[]): { id: string; name: string }[] {
  const seen = new Map<string, string>();
  for (const i of items) seen.set(i.author.id, i.author.name);
  return [...seen].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
}
